import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import Api from 'api';
import { SimpleModal } from 'components/Modal';
import { TopBarRedesigned } from 'components/TopBarRedesigned';
import { CrossButtonBlack } from 'components/Buttons/CrossButton';
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles';
import { ChatbaseWidget, openChatbaseWidget, consumeChatbaseSupportRequest, type ChatbaseStatus, type ChatbaseUserContext } from 'components/ChatWidget';
import { exitReact } from 'utils';
import { 
    Container, 
    KnowledgeBaseButton,
    TitleContainer,
    PopupTitle,
    ModalText,
    OrDivider,
    SearchButton,
    SearchWrapper,
    SearchIconWrapper,
    StyledTextField,
    ExpandedSection,
    LabelContent,
    QueryTypeLabel,
    GeneralSupportButton,
    EmergencySupportButton,
    SupportOptionDescription,
    HeaderSubtitle,
    VirtualSupportBanner,
    BannerHeader,
    BannerText,
    BannerTitle,
    BannerSubtitle,
    BetaBadge,
    SelfServiceTitle,
    FormSection,
    FormField,
    FormLabel,
    FormInput,
    FormTextArea,
    SubmitButton,
    LegalSection,
    TechnicalSection,
    SectionTitle,
    SectionContent,
    SectionLink,
    SectionText,
    SectionBullet,
    SupportOptionsContainer,
    SelfServiceCard,
    SelfServiceSubtitle,
    KnowledgeBaseSection,
    StillNeedHelpCard,
    LegalTechnicalCard,
    PageTitle,
    ChatIcon,
    WarningIcon,
    SearchInputGroup,
    SearchErrorText
} from '../../pages/hygienist/ContactSupport/ContactSupport.styles';
import { CheckCircle, Description, Build, Search } from '@mui/icons-material';

type ChatbaseRole = NonNullable<ChatbaseUserContext['metadata']>['role'];

interface ContactSupportBaseProps {
    title?: string;
    onBack?: () => void;
    hideNotificationBell?: boolean;
    /** T2-6194: parent renders TalentNavbar + TalentBackButton; skip legacy TopBar. */
    hideTopBar?: boolean;
}

const ContactSupportBase: React.FC<ContactSupportBaseProps> = ({ 
    title = "Need Help?",
    onBack = () => exitReact('home'),
    hideNotificationBell = false,
    hideTopBar = false,
}) => {
    const [expandedSection, setExpandedSection] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [generalSubject, setGeneralSubject] = useState('');
    const [generalMessage, setGeneralMessage] = useState('');
    const [emergencySubject, setEmergencySubject] = useState('');
    const [emergencyMessage, setEmergencyMessage] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [chatWidgetLoaded, setChatWidgetLoaded] = useState(false);
    const [chatWidgetError, setChatWidgetError] = useState(false);
    const [chatStatus, setChatStatus] = useState<ChatbaseStatus>('loading');
    const [chatbaseUserContext, setChatbaseUserContext] = useState<ChatbaseUserContext | null>(null);

    useEffect(() => {
        if (consumeChatbaseSupportRequest()) setExpandedSection('general');
    }, []);

    // Refs to scroll/focus into the correct form on selection
    const generalMessageRef = useRef<HTMLTextAreaElement | null>(null);
    const emergencyMessageRef = useRef<HTMLTextAreaElement | null>(null);
    const generalSectionRef = useRef<HTMLDivElement | null>(null);
    const emergencySectionRef = useRef<HTMLDivElement | null>(null);

    const toggleGeneral = () => {
        const willOpen = expandedSection !== 'general';
        setExpandedSection(willOpen ? 'general' : null);
        if (willOpen) {
            setTimeout(() => {
                generalSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                generalMessageRef.current?.focus();
            }, 150);
        }
    };

    const toggleEmergency = () => {
        const willOpen = expandedSection !== 'emergency';
        setExpandedSection(willOpen ? 'emergency' : null);
        if (willOpen) {
            setTimeout(() => {
                emergencySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                emergencyMessageRef.current?.focus();
            }, 150);
        }
    };

    useEffect(() => {
        const target = expandedSection === 'general' ? generalMessageRef.current : expandedSection === 'emergency' ? emergencyMessageRef.current : null;
        if (!target) return;
        // Delay slightly to allow expansion animation to apply height
        const t = setTimeout(() => {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            target.focus();
        }, 150);
        return () => clearTimeout(t);
    }, [expandedSection]);

    useEffect(() => {
        document.body.classList.add('react-support-page');
        try {
            (window as any)?.app?.closePanel?.();
        } catch {}

        return () => {
            document.body.classList.remove('react-support-page');
        };
    }, []);

    const handleBack = useCallback(() => {
        document.body.classList.remove('react-support-page');
        onBack();
    }, [onBack]);

    // Cordova-safe links and support URLs
    const SUPPORT_BASE = 'https://support.tempstars.com';
    const KNOWLEDGE_URL = SUPPORT_BASE + '/';
    const searchUrl = useMemo(() => (
        `${SUPPORT_BASE}/hc/en-us/search?utf8=%E2%9C%93&query=${encodeURIComponent(searchQuery)}`
    ), [searchQuery]);

    const onInfoLinkClick = useCallback((infoLink: string) => {
        // @ts-ignore
        // Prefer Cordova InAppBrowser if available
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.Cordova ? cordova.InAppBrowser.open(
                infoLink,
                '_system',
                'location=yes'
            )
            : window.open(
                infoLink,
                '_blank',
                'location=yes'
            );
    }, []);

    const handleSearch = useCallback(() => {
        if (!searchQuery.trim()) {
            setSearchError('Please enter your search term');
            return;
        }
        setSearchError('');
        onInfoLinkClick(searchUrl);
    }, [onInfoLinkClick, searchUrl, searchQuery]);
    const openKnowledgeBase = useCallback(() => onInfoLinkClick(KNOWLEDGE_URL), [onInfoLinkClick]);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        if (searchError) {
            setSearchError('');
        }
    };

    useEffect(() => {
        try {
            const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
            if (!currentUser) return;
            const displayName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();
            let role: ChatbaseRole = 'member';
            if (currentUser?.hygienist) {
                role = 'hygienist';
            } else if (currentUser?.dentist) {
                role = 'dentist';
            } else if (currentUser?.assistant) {
                role = 'assistant';
            }
            setChatbaseUserContext({
                userId: currentUser?.id ? String(currentUser.id) : undefined,
                metadata: {
                    name: displayName || undefined,
                    email: currentUser?.email,
                    role,
                },
            });
        } catch (error) {
            console.warn('Unable to derive Chatbase user context', error);
        }
    }, []);

    const handleChatWidgetLoad = useCallback(() => {
        setChatWidgetLoaded(true);
        setChatWidgetError(false);
    }, []);

    const handleChatWidgetError = useCallback((error: Error) => {
        console.error('Chatbase widget failed to load', error);
        setChatWidgetError(true);
    }, []);

    const openGeneralSupport = useCallback(() => {
        setExpandedSection('general');
    }, []);

    const handleLaunchAiAssistant = useCallback(() => {
        if (chatWidgetError || !openChatbaseWidget()) openGeneralSupport();
    }, [chatWidgetError, openGeneralSupport]);

    // Dynamic app/device/browser details for Technical Information section
    const w: any = window as any;
    const isCordova = !!w?.cordova;
    const appVersion: string = w?.TempStars?.version || 'N/A';
    const platformInfo: string = isCordova
        ? `Platform: ${w?.device?.platform || ''} ${w?.device?.version || ''}`
        : `Browser: ${navigator.userAgent}`;
    const modelOrOsInfo: string = isCordova
        ? `Model: ${(w?.device?.manufacturer || '')} ${(w?.device?.model || '')}`
        : `OS: ${navigator.platform}`;

    const handleSubmit = async () => {
        if (!expandedSection) return;
        
        const questionType = expandedSection === 'emergency' ? 'emergency' : 'general';
        const text = (expandedSection === 'general' ? generalMessage : emergencyMessage).trim();
        
        if (!text) {
            alert('Please provide a message so we can assist you.');
            return;
        }

        setIsSubmitting(true);
        try {
            await Api.contactSupport({
                questionType,
                text,
            });
            
            setShowSuccessModal(true);
            
            if (expandedSection === 'general') {
                setGeneralSubject('');
                setGeneralMessage('');
            } else {
                setEmergencySubject('');
                setEmergencyMessage('');
            }
            setExpandedSection(null);
        } catch (error) {
            console.error('Error submitting support request:', error);
            alert('There was an error submitting your request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const onSuccessModalClose = () => {
        setShowSuccessModal(false);
        setGeneralSubject('');
        setGeneralMessage('');
        setEmergencySubject('');
        setEmergencyMessage('');
        setExpandedSection(null);
    };

    const handleKnowledgeBase = () => {
        openKnowledgeBase();
    };

    return (
        <>
            <ChatbaseWidget
                userContext={chatbaseUserContext}
                onLoad={handleChatWidgetLoad}
                onError={handleChatWidgetError}
                onStatusChange={setChatStatus}
                onContactSupport={openGeneralSupport}
            />
            {!hideTopBar ? (
                <TopBarRedesigned title={title} onBack={handleBack} hideNotificationBell={hideNotificationBell} />
            ) : null}
            <Container $withTalentNav={hideTopBar}>
                {hideTopBar ? <PageTitle>{title}</PageTitle> : null}
                <HeaderSubtitle>
                    We're here to help you get the most out of TempStars
                </HeaderSubtitle>

                <VirtualSupportBanner>
                    <BannerHeader>
                        <CheckCircle sx={{ color: 'white', fontSize: 24 }} />
                        <BannerText>
                            <BannerTitle>
                                {chatWidgetError
                                    ? 'AI Support Unavailable'
                                    : chatStatus === 'ready'
                                        ? 'AI Support Ready'
                                        : chatStatus === 'opening'
                                            ? 'Opening AI Support'
                                            : chatWidgetLoaded ? 'AI Support' : 'Loading AI Support'}
                            </BannerTitle>
                            <BetaBadge>
                                {chatWidgetError ? 'UNAVAILABLE' : 'BETA'}
                            </BetaBadge>
                        </BannerText>
                    </BannerHeader>
                    <BannerSubtitle>
                        {chatWidgetError && 'AI chat is temporarily unavailable. Please use the forms below and our team will follow up.'}
                        {!chatWidgetError && !chatWidgetLoaded && 'Setting up your AI assistant...'}
                        {!chatWidgetError && chatWidgetLoaded && 'Chat with our AI assistant, or contact our team below.'}
                    </BannerSubtitle>
                    <StyledButton
                        variant="contained"
                        size="large"
                        onClick={handleLaunchAiAssistant}
                        disabled={!chatWidgetLoaded && !chatWidgetError}
                        data-testid="support-chat-or-contact"
                        style={{ marginTop: '0.75rem' }}
                        className="no-fastclick"
                    >
                        {chatWidgetError ? 'Contact Support' : '💬 Chat with AI Assistant'}
                    </StyledButton>
                </VirtualSupportBanner>

                <SelfServiceCard>
                    <SelfServiceTitle>🔍 Find Your Answer</SelfServiceTitle>
                    <SelfServiceSubtitle>
                        Search our extensive Member Support Database for quick solutions:
                    </SelfServiceSubtitle>
                    
                    <SearchWrapper>
                        <SearchInputGroup>
                            <SearchIconWrapper>
                                <Search />
                            </SearchIconWrapper>
                            <StyledTextField
                                placeholder="Enter your search term..."
                                value={searchQuery}
                                onChange={handleSearchQueryChange}
                                onKeyPress={handleKeyPress}
                                aria-invalid={!!searchError}
                                aria-describedby={searchError ? 'support-search-error' : undefined}
                                className="no-fastclick"
                            />
                        </SearchInputGroup>
                        <SearchButton onClick={handleSearch} className="no-fastclick">
                            Search
                        </SearchButton>
                    </SearchWrapper>
                    {searchError && (
                        <SearchErrorText id="support-search-error" role="alert">
                            {searchError}
                        </SearchErrorText>
                    )}

                    <KnowledgeBaseSection>
                        <OrDivider>OR</OrDivider>
                        <KnowledgeBaseButton 
                            onClick={handleKnowledgeBase}
                            className="no-fastclick"
                        >
                            Access our entire Support Knowledge Base here
                        </KnowledgeBaseButton>
                    </KnowledgeBaseSection>
                </SelfServiceCard>

                <StillNeedHelpCard>
                    <TitleContainer>
                        <PopupTitle>💬 Still Need Help?</PopupTitle>
                    </TitleContainer>
                    <ModalText>Choose the best way to get in touch with our support team:</ModalText>

                    <SupportOptionsContainer>
                        <GeneralSupportButton 
                            onClick={toggleGeneral}
                            $expanded={expandedSection === 'general'}
                            className="no-fastclick"
                        >
                            <LabelContent>
                                <QueryTypeLabel><ChatIcon />General Question</QueryTypeLabel>
                                <SupportOptionDescription>
                                    Get help with account issues, billing questions, or general platform guidance
                                </SupportOptionDescription>
                            </LabelContent>
                        </GeneralSupportButton>

                        <EmergencySupportButton 
                            onClick={toggleEmergency}
                            $expanded={expandedSection === 'emergency'}
                            className="no-fastclick"
                        >
                            <LabelContent>
                                <QueryTypeLabel><WarningIcon />Emergency Support</QueryTypeLabel>
                                <SupportOptionDescription>
                                    Urgent problems that need immediate attention - we'll prioritize your request
                                </SupportOptionDescription>
                            </LabelContent>
                        </EmergencySupportButton>
                    </SupportOptionsContainer>

                    {expandedSection === 'general' && (
                        <ExpandedSection $expanded={true} ref={generalSectionRef}>
                            <FormSection>
                                <FormField>
                                    <FormLabel>Subject</FormLabel>
                                    <FormInput
                                        type="text"
                                        value="Help Widget Inquiry"
                                        readOnly
                                        disabled
                                        className="no-fastclick"
                                    />
                                </FormField>
                                <FormField>
                                    <FormLabel>Message</FormLabel>
                                    <FormTextArea
                                        placeholder="Please provide as much detail as possible to help us resolve your issue quickly..."
                                        value={generalMessage}
                                        onChange={(e) => setGeneralMessage(e.target.value)}
                                        rows={4}
                                        ref={generalMessageRef}
                                        className="no-fastclick"
                                    />
                                </FormField>
                                <SubmitButton 
                                    onClick={handleSubmit} 
                                    disabled={isSubmitting}
                                    className="no-fastclick"
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                                </SubmitButton>
                            </FormSection>
                        </ExpandedSection>
                    )}

                    {expandedSection === 'emergency' && (
                        <ExpandedSection $expanded={true} ref={emergencySectionRef}>
                            <FormSection>
                                <FormField>
                                    <FormLabel>Subject</FormLabel>
                                    <FormInput
                                        type="text"
                                        value="Help Widget Emergency"
                                        readOnly
                                        disabled
                                        className="no-fastclick"
                                    />
                                </FormField>
                                <FormField>
                                    <FormLabel>Message</FormLabel>
                                    <FormTextArea
                                        placeholder="Please provide as much detail as possible about your urgent issue..."
                                        value={emergencyMessage}
                                        onChange={(e) => setEmergencyMessage(e.target.value)}
                                        rows={4}
                                        ref={emergencyMessageRef}
                                        className="no-fastclick"
                                    />
                                </FormField>
                                <SubmitButton 
                                    onClick={handleSubmit} 
                                    disabled={isSubmitting}
                                    className="no-fastclick"
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                                </SubmitButton>
                            </FormSection>
                        </ExpandedSection>
                    )}
                </StillNeedHelpCard>

                <LegalTechnicalCard>
                    <LegalSection>
                        <SectionTitle>
                            <Description sx={{ color: 'var(--text-secondary)', fontSize: 20 }} />
                            Legal Information
                        </SectionTitle>
                        <SectionContent>
                            <SectionText>
                                For our most recent <strong>Terms of Service</strong>, visit{' '}
                                <SectionLink href="https://www.tempstars.ca/terms" onClick={(e) => { e.preventDefault(); onInfoLinkClick('https://www.tempstars.ca/terms'); }}>
                                    www.tempstars.ca/terms
                                </SectionLink>
                            </SectionText>
                            <SectionText>
                                For our most recent <strong>Privacy Policy</strong>, visit{' '}
                                <SectionLink href="https://www.tempstars.ca/privacy" onClick={(e) => { e.preventDefault(); onInfoLinkClick('https://www.tempstars.ca/privacy'); }}>
                                    www.tempstars.ca/privacy
                                </SectionLink>
                            </SectionText>
                        </SectionContent>
                    </LegalSection>

                    <TechnicalSection>
                        <SectionTitle>
                            <Build sx={{ color: 'var(--text-secondary)', fontSize: 20 }} />
                            Technical Information
                        </SectionTitle>
                        <SectionContent>
                            <SectionBullet>App Version: {appVersion}</SectionBullet>
                            <SectionBullet>{platformInfo}</SectionBullet>
                            <SectionBullet>{modelOrOsInfo}</SectionBullet>
                        </SectionContent>
                    </TechnicalSection>
                </LegalTechnicalCard>

                <SimpleModal
                    isOpen={showSuccessModal}
                    onClose={onSuccessModalClose}
                >
                    <>
                        <TitleContainer>
                            <PopupTitle>Request Submitted Successfully!</PopupTitle>
                            <CrossButtonBlack onClick={onSuccessModalClose} />
                        </TitleContainer>
                        <ModalText>
                            Thank you for contacting us. We'll get back to you as soon as possible.
                        </ModalText>
                        <StyledButton
                            variant="contained"
                            size="large"
                            onClick={onSuccessModalClose}
                            className="no-fastclick"
                        >
                            Ok
                        </StyledButton>
                    </>
                </SimpleModal>
            </Container>
        </>
    );
};

export default ContactSupportBase;
